import { getAccessToken, getLob } from './authenticationService';
import axios from 'axios';
import { EnvironmentRegistry } from './registry/environmentRegistry';

export interface ReportSearchParams {
  report: string;
  page?: number;
  pageSize?: number;
  contains?: string;
  filters?: Record<string, string[]>;
  
}

export interface LiveReportDownloadRequest {
  configName: string;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  period?: string;
  year?: string;
  filters?: {
    map: Record<string, string[]>;
  };
  distributorFilter?: {
    locationFilters?: {
      level: string;
      value: string;
    }[];
    userFilters?: {
      userId: string;
      direct: boolean;
    }[];
  };
  format: string;
}



export interface ReportFilterParams { 
  report: string;
  which: string;
  contains?: string;
  page?: number;
  pageSize?: number;
  isDistributorview?: boolean;
  filters?: Record<string, string[]>;
  additionalFilters?: Record<string, string[]>;
  since?: string;
  until?: string;
  distributorFilter?: {
    locationFilters?: {
      level: string;
      value: string;
    }[];
    userFilters?: {
      userId: string;
      direct: boolean;
    }[];
  };
}

export interface ReportSearchResponse {
  items: any[];
  total: number;
  totalPages: number;
  currentPage: number;
}

export interface ReportFilterValuesResponse {
  values: Record<string, string[]>;
}

const env = EnvironmentRegistry.getEnvironment();
let DATASTREAM_BASE_URL = '';
if(env === 'prod'){
  DATASTREAM_BASE_URL = 'https://datastream.salescode.ai';
}else if(env === 'demo'){
  DATASTREAM_BASE_URL = 'https://datastream-demo.salescode.ai';
}else if(env === 'uat'){
  DATASTREAM_BASE_URL = 'https://datastream-demo.salescode.ai';
}

/**
 * Fetch sales hierarchy designations (levels)
 */
export async function fetchSalesDesignations(parentDesignation: string = 'admin', scope: string = 'all'): Promise<string[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const url = `${DATASTREAM_BASE_URL}/org/users/designations/by-parent?designation=${encodeURIComponent(parentDesignation)}&scope=${encodeURIComponent(scope)}`;
  
  try {
    const response = await axios.get<{ designations: string[]; parentDesignation: string; scope: string; tenant: string }>(url, { headers });
    return response.data.designations || [];
  } catch (error: any) {
    return [];
  }
}

/**
 * Fetch users by designation
 */
export async function fetchUsersByDesignation(designation: string): Promise<string[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const url = `${DATASTREAM_BASE_URL}/org/users/by-designation?designation=${encodeURIComponent(designation)}`;
  
  try {
    const response = await axios.get<{ designation: string; users: string[]; tenant: string }>(url, { headers });
    return response.data.users || [];
  } catch (error: any) {
    return [];
  }
}


export interface ColumnOption {
  alias: string;
  display: string;
}

/**
 * Fetch column definitions for a report (order and display names)
 */
export async function fetchColumnDefinitions(reportName: string): Promise<ColumnOption[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const url = `${DATASTREAM_BASE_URL}/report-defs/fields?report=${encodeURIComponent(reportName)}`;
  
  try {
    const response = await axios.get<{
      fields?: {
        columns?: ColumnOption[];
      };
    }>(url, { headers });
    
    // Extract columns from the response
    const columns = response.data?.fields?.columns;
    
    if (columns && Array.isArray(columns)) {
      return columns;
    }
    
    return [];
  } catch (error: any) {
    console.error('Error fetching column definitions:', error);
    return [];
  }
}


/**
 * Fetch geographical location levels
 */
export async function fetchGeographicalLevels(): Promise<string[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const url = `${DATASTREAM_BASE_URL}/org/users/location/levels`;
  
  try {
    const response = await axios.get<{ levelsLowToHigh: string[]; tenant: string }>(url, { headers });
    return response.data.levelsLowToHigh || [];
  } catch (error: any) {
    return [];
  }
}

/**
 * Fetch geographical locations by level
 */
export async function fetchGeographicalLocations(level: string): Promise<string[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const url = `${DATASTREAM_BASE_URL}/org/locations?parentLevel=${encodeURIComponent(level)}`;
  
  try {
    const response = await axios.get<{ locations?: string[]; values?: string[]; level: string; tenant: string }>(url, { headers });
    const locations = response.data.locations || response.data.values || [];
    return locations;
  } catch (error: any) {
    console.error('Error fetching geographical locations:', error);
    return [];
  }
}

/**
 * Fetch geographical locations under a parent (drill-down)
 */
export async function fetchGeographicalLocationsUnder(
  parentLevel: string,
  parentValue: string,
  childLevel: string
): Promise<string[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const url = `${DATASTREAM_BASE_URL}/org/users/location/under?parentLevel=${encodeURIComponent(parentLevel)}&parentValue=${encodeURIComponent(parentValue)}&childLevel=${encodeURIComponent(childLevel)}`;
  
  
  try {
    const response = await axios.get<any>(url, { headers });
    
    const locations = response.data?.values || 
                      response.data?.locations || 
                      [];
    
    return Array.isArray(locations) ? locations : [];
  } catch (error: any) {
    return [];
  }
}

/**
 * Fetch report data with search and filters
 */
export async function fetchReportData(params: any): Promise<ReportSearchResponse> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const url = `${DATASTREAM_BASE_URL}/rpt-generic/search`;

  const payload: any = {
    report: params.report,
    count: true
  };

  if (params.page !== undefined) {
    payload.page = params.page;
  }
  
  if (params.pageSize !== undefined) {                        
    payload.pageSize = params.pageSize;
  } 
  
  if (params.contains && params.contains.trim().length > 0) {
    payload.contains = params.contains.trim();
  }
  
  // Add date range filters (since and until)
  if (params.since) {
    payload.since = params.since;
  }
  
  if (params.until) {
    payload.until = params.until;
  }

  

  const commaSeparatedFilters = params.commaSeparatedFilters || [];
  if (params.filters) {
    Object.entries(params.filters).forEach(([filterType, options]) => {
      if (options && Array.isArray(options) && options.length > 0) {
        if (commaSeparatedFilters.includes(filterType)) {
          const commaSeparated = options.join(',');
          payload[filterType] = commaSeparated;
        } else {
          // For other filters, send each value separately
          options.forEach((optionValue: string) => {
            payload[filterType] = optionValue;
          });
        }
      }
    });    
  }
  const hasDirectDistributorSelection = params.filters?.distributor_code && 
                                        Array.isArray(params.filters.distributor_code) && 
                                        params.filters.distributor_code.length > 0;
  
  if (params.distributorFilter && !hasDirectDistributorSelection) {
    payload.distributorFilter = params.distributorFilter;
  }
  
  try {
    const response = await axios.post<ReportSearchResponse>(url, payload, { headers });
    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Fetch filter values for a specific column
 */
export async function fetchFilterValues(params: ReportFilterParams): Promise<string[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }
  const url = `${DATASTREAM_BASE_URL}/rpt-generic/filter-values`;

  // Build POST payload (aligned with download payload style)
  const payload: any = {
    report: params.report,
    which: params.which
  };

  if (params.contains && params.contains.trim().length > 0) {
    payload.contains = params.contains.trim();
  }

  if (params.since !== undefined) {
    payload.since = params.since;
  }

  if (params.until !== undefined) {
    payload.until = params.until;
  }

  if (params.additionalFilters && Object.keys(params.additionalFilters).length > 0) {
    Object.entries(params.additionalFilters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        if (key === 'distributor_code') {
          payload[key] = values.join(',');
        } else {
          payload[key] = values;
        }
      }
    });
  }

  if (params.filters && Object.keys(params.filters).length > 0) {
    Object.entries(params.filters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        payload[key] = values.join(',');
      }
    });
  }
  const hasDirectDistributorInFilters = params.filters?.distributor_code && 
                                         Array.isArray(params.filters.distributor_code) && 
                                         params.filters.distributor_code.length > 0;
  const hasDirectDistributorInAdditional = params.additionalFilters?.distributor_code && 
                                           Array.isArray(params.additionalFilters.distributor_code) && 
                                           params.additionalFilters.distributor_code.length > 0;
  const hasDirectDistributorSelection = hasDirectDistributorInFilters || hasDirectDistributorInAdditional;
  if (params.distributorFilter && !hasDirectDistributorSelection) {
    payload.distributorFilter = params.distributorFilter;
  }
  
  try {
    const response = await axios.post<ReportFilterValuesResponse>(url, payload, { headers });
    const values = response.data?.values?.[params.which] || [];
    return Array.isArray(values) ? values : [];
  } catch (error: any) {
    return [];
  }
}

export async function fetchChannelValues(params: ReportFilterParams): Promise<string[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID':  tenantId || '' 
  };
  
  if (token) {
    headers['Authorization'] =  token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const queryParams = new URLSearchParams({
    report: params.report,
    which: params.which,
  });
  
  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString());
  }
  
  if (params.pageSize !== undefined) {
    queryParams.append('pageSize', params.pageSize.toString());
  }
  
  const url = `${DATASTREAM_BASE_URL}/table-generic/filter-values?${queryParams.toString()}`;
  
  try {
    const response = await axios.get<ReportFilterValuesResponse>(url, { headers });
    const values = response.data?.values?.[params.which] || [];
    return Array.isArray(values) ? values : [];
  } catch (error: any) {
    return [];
  }
}

/**
 * Fetch children users by userId and designation
 */
export async function fetchChildrenUsers(userId: string, designation: string = 'supplier'): Promise<string[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const url = `${DATASTREAM_BASE_URL}/org/users/children?userId=${encodeURIComponent(userId)}&designation=${encodeURIComponent(designation)}`;
  
  try {
    const response = await axios.get<{ users?: string[]; children?: string[]; userId: string; designation: string; tenant: string }>(url, { headers });
    return response.data.users || [];
  } catch (error: any) {
    return [];
  }
}

/**
 * Fetch location users by level, value and designation
 */
export async function fetchLocationUsers(level: string, value: string, designation: string = 'supplier'): Promise<string[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const url = `${DATASTREAM_BASE_URL}/org/users/location/users?level=${encodeURIComponent(level)}&value=${encodeURIComponent(value)}&designation=${encodeURIComponent(designation)}`;
  
  try {
    const response = await axios.get<{ users: string[]; level: string; value: string; designation: string; tenant: string }>(url, { headers });
    return response.data.users || [];
  } catch (error: any) {
    return [];
  }
}

export interface FilterOption {
  alias: string;
  display: string;
}

/**
 * Fetch available custom filters for a report
 */
export async function fetchAvailableFilters(reportName: string): Promise<FilterOption[]> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  const url = `${DATASTREAM_BASE_URL}/report-defs/fields?report=${encodeURIComponent(reportName)}`;
  
  try {
    const response = await axios.get<{
      fields?: {
        filters?: {
          names?: string[];
          map?: Record<string, FilterOption>;
        };
      };
    }>(url, { headers });
    
    // Extract filters from the new structure
    const filtersMap = response.data?.fields?.filters?.map;
    
    if (filtersMap) {
      // Convert map object to array of FilterOption
      return Object.values(filtersMap);
    }
    
    return [];
  } catch (error: any) {
    console.error('Error fetching available filters:', error);
    return [];
  }
}

/**
 * Download report in specified format (New Flow)
 */
export interface SnapshotReportDownloadRequest {
  reportName: string;
  filters?: {
    map: Record<string, string[]>;
  };
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  format?: string;
  distributorFilter?: {
    locationFilters?: {
      level: string;
      value: string;
    }[];
    userFilters?: {
      userId: string;
      direct: boolean;
    }[];
  };
}

export async function downloadSnapshotReport(params: SnapshotReportDownloadRequest): Promise<Blob> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };
  
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }
  
  const url = `${DATASTREAM_BASE_URL}/rpt-generic/download`;

  
  try {
    
    const response = await axios.post(
      url,
      params, 
      {
        headers: headers,
        responseType: 'blob'
      }
    );
    
    return response.data;
  } catch (error: any) {
    if (error.response) {
      if (error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          try {
            const errorData = JSON.parse(errorText);
            const errorMessage = errorData.error || errorData.message || 'Download failed';
            throw new Error(errorMessage);
          } catch (parseError) {
            throw new Error(errorText || 'Download failed');
          }
        } catch (textError) {
          throw new Error('Download failed with unknown error');
        }
      } else {
        const errorMessage = error.response.data?.error || error.response.data?.message || `Download failed with status ${error.response.status}`;
        throw new Error(errorMessage);
      }
    } else if (error.request) {
      throw new Error('No response from server. Please check your network connection.');
    } else {
      throw new Error(error.message || 'Download request failed');
    }
  }
}


export async function downloadLiveReport(params: LiveReportDownloadRequest): Promise<Blob> {
  const token = getAccessToken();
  const tenantId = getLob();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId || ''
  };

  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }
  
  const url = `${DATASTREAM_BASE_URL}/live/download?attachment=true`;
  
  try {
    
    const response = await axios.post(
      url,
      params, 
      {
        headers: headers,
        responseType: 'blob'
      }
    );
    
    return response.data;
  } catch (error: any) {
    if (error.response) {
      if (error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          try {
            const errorData = JSON.parse(errorText);
            const errorMessage = errorData.error || errorData.message || 'Download failed';
            throw new Error(errorMessage);
          } catch (parseError) {
            throw new Error(errorText || 'Download failed');
          }
        } catch (textError) {
          throw new Error('Download failed with unknown error');
        }
      } else {
        const errorMessage = error.response.data?.error || error.response.data?.message || `Download failed with status ${error.response.status}`;
        throw new Error(errorMessage);
      }
    } else if (error.request) {
      throw new Error('No response from server. Please check your network connection.');
    } else {
      throw new Error(error.message || 'Download request failed');
    }
  }
}